// 통합 워크데스크 /flow (unified-trade-flow FR-5·6·7) — 거래 생애주기를 한 페이지에서:
//   ① 시뮬레이션(가격안 비교) → ② 계약(조건·타임라인) → ③ 선적 딜(10단계 실무·서류) → ④ 실적(달성률·마진)
// 좌측 = 케이스(계약 / 협상 중 시뮬레이션 / 통합 현황), 본문 = 4단계 레일.
// URL 상태: ?case=ct:{uuid} | sim:{product}:{country} | all & stage=1..4 & deal={uuid}
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  contractsRepo,
  contractSalesRepo,
  dealsRepo,
  isSupabaseConfigured,
  partnersRepo,
  productsRepo,
  type NewContract,
} from "@/lib/db";
import type { ContractRow, ContractSale, Deal, Partner, Product } from "@/types";
import type { CountrySim, DeskState, Offer, ProductId } from "@/lib/margin/model";
import {
  contractProgress,
  effectiveTiers,
  loadState,
  pct,
  PRODUCTS,
  saveState,
  usd,
} from "@/lib/margin/model";
import { contractRowToModel, saleToModel } from "@/flow/convert";
import Simulator from "@/flow/Simulator";
import ContractCard from "@/flow/ContractCard";
import ContractLedger from "@/flow/ContractLedger";
import ConsolidatedView from "@/flow/ConsolidatedView";
import ManagementDeck from "@/flow/ManagementDeck";
import ActionHome from "@/flow/ActionHome";
import TimelineBoard from "@/flow/TimelineBoard";
import { todayKst } from "@/lib/timeline/scale";
import { DealWorkspace } from "./DealWorkspacePage";
import { ConfigBanner } from "@/components/ConfigBanner";

type FlowCase =
  | { kind: "ct"; id: string }
  | { kind: "sim"; product: ProductId; country: string }
  | { kind: "all" }
  | { kind: "tl" }
  | { kind: "home" };

const STAGES = [
  { no: 1, label: "시뮬레이션", hint: "계약 전 — 가격안 손익 비교" },
  { no: 2, label: "계약", hint: "확정 조건 · MOQ 쿼터 · 타임라인" },
  { no: 3, label: "선적 딜", hint: "10단계 수출실무 · 서류 · 게이트" },
  { no: 4, label: "실적", hint: "달성률 · 실현 마진 원장" },
] as const;

const STATUS_LABEL: Record<Deal["status"], string> = {
  quote: "견적",
  order: "주문",
  ship_prep: "선적준비",
  doc_issued: "서류발행",
  customs_shipping: "통관/발송",
  done: "완료",
  cancelled: "취소",
};

function parseCase(v: string | null): FlowCase {
  if (v?.startsWith("ct:")) return { kind: "ct", id: v.slice(3) };
  if (v?.startsWith("sim:")) {
    const [, product, ...rest] = v.split(":");
    if (PRODUCTS.some((p) => p.id === product)) return { kind: "sim", product: product as ProductId, country: rest.join(":") };
  }
  if (v === "all") return { kind: "all" };
  if (v === "tl") return { kind: "tl" };
  return { kind: "home" }; // 기본 진입 = 오늘의 액션 홈 (경영뷰 아님)
}
const caseKey = (c: FlowCase) =>
  c.kind === "ct"
    ? `ct:${c.id}`
    : c.kind === "sim"
      ? `sim:${c.product}:${c.country}`
      : c.kind === "all"
        ? "all"
        : c.kind === "tl"
          ? "tl"
          : "home";

export function FlowPage(): JSX.Element {
  const [searchParams, setSearchParams] = useSearchParams();
  const cs = parseCase(searchParams.get("case"));
  const stage = Math.min(4, Math.max(1, Number(searchParams.get("stage")) || (cs.kind === "sim" ? 1 : cs.kind === "ct" ? 2 : 1)));
  const dealId = searchParams.get("deal");

  const setUrl = useCallback(
    (next: { case?: FlowCase; stage?: number; deal?: string | null }) => {
      setSearchParams(
        (prev) => {
          const p = new URLSearchParams(prev);
          if (next.case) p.set("case", caseKey(next.case));
          if (next.stage != null) p.set("stage", String(next.stage));
          if (next.deal === null) p.delete("deal");
          else if (next.deal != null) p.set("deal", next.deal);
          // 케이스 이동 시 딜 선택 해제 — 단 deal 을 함께 지정한 호출은 그 딜을 지킨다.
          // (가드 없이 두면 setUrl({case, deal}) 이 바로 위에서 set 한 딜을 도로 지운다)
          if (next.case && next.deal === undefined) p.delete("deal");
          return p;
        },
        { replace: false },
      );
    },
    [setSearchParams],
  );

  // ── 데이터 로드 ──
  const [desk, setDesk] = useState<DeskState | null>(null);
  const [contracts, setContracts] = useState<ContractRow[]>([]);
  const [salesAll, setSalesAll] = useState<ContractSale[]>([]);
  const [deals, setDeals] = useState<Deal[]>([]);
  const [partners, setPartners] = useState<Partner[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [saveMsg, setSaveMsg] = useState("");

  const refreshDb = useCallback(() => {
    if (!isSupabaseConfigured()) return;
    Promise.all([contractsRepo.list(), contractSalesRepo.list(), dealsRepo.list(), partnersRepo.list(), productsRepo.list()])
      .then(([cts, ss, ds, ps, prods]) => {
        setContracts(cts);
        setSalesAll(ss);
        setDeals(ds);
        setPartners(ps);
        setProducts(prods);
      })
      .catch((e) => setError(e instanceof Error ? e.message : String(e)));
  }, []);

  useEffect(() => {
    void loadState().then(({ state }) => setDesk(state));
    refreshDb();
  }, [refreshDb]);

  // 시뮬레이션 상태 디바운스 자동 저장 (마진 데스크 UX 승계)
  const timer = useRef<number | null>(null);
  const skipFirst = useRef(true);
  useEffect(() => {
    if (!desk) return;
    if (skipFirst.current) {
      skipFirst.current = false;
      return;
    }
    setSaveMsg("저장 중…");
    if (timer.current) window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => {
      void saveState(desk).then((ok) => setSaveMsg(ok ? "저장됨" : "로컬만 저장됨(서버 미접속)"));
    }, 1200);
    return () => {
      if (timer.current) window.clearTimeout(timer.current);
    };
  }, [desk]);

  // KST 기준 오늘. new Date().toISOString() 은 UTC라 오전 9시 이전엔 **어제**를 준다 (Design §5.3).
  const today = todayKst();

  // ── 파생 데이터 ──
  const selectedContract = cs.kind === "ct" ? contracts.find((c) => c.id === cs.id) ?? null : null;
  const contractSales = useMemo(
    () => (selectedContract ? salesAll.filter((s) => s.contract_id === selectedContract.id) : []),
    [salesAll, selectedContract],
  );
  const contractDeals = useMemo(
    () => (selectedContract ? deals.filter((d) => d.contract_id === selectedContract.id) : []),
    [deals, selectedContract],
  );
  const progress = selectedContract
    ? contractProgress(contractRowToModel(selectedContract), contractSales.map(saleToModel))
    : null;

  // 확정 대기: 완료(done) 딜 중 아직 실적 행이 없는 것 (ContractLedger.pendingDeals 와 동일 규칙).
  // 임베드 딜에서 onChanged→refreshDb 로 contractSales/contractDeals 가 갱신되면 이 배지도 즉시 반영된다.
  const confirmedDealIds = useMemo(() => new Set(contractSales.map((s) => s.deal_id).filter(Boolean)), [contractSales]);
  const pendingConfirmCount = contractDeals.filter((d) => d.status === "done" && !confirmedDealIds.has(d.id)).length;

  // 시뮬레이션 케이스 목록 (제품 × 국가)
  const simCases = useMemo(() => {
    if (!desk) return [];
    return PRODUCTS.flatMap((p) =>
      desk.products[p.id].countries.map((c) => ({ product: p.id, productName: p.name, country: c.id, countryName: c.name })),
    );
  }, [desk]);

  // ── ①→② 계약 등록 (FR-7) ──
  async function registerContract(productId: ProductId, c: CountrySim, o: Offer) {
    if (!isSupabaseConfigured()) {
      window.alert("Supabase 미설정 — 계약 등록은 DB 연결 후 가능합니다.");
      return;
    }
    const ts = effectiveTiers(o, c);
    const lastTo = ts.reduce<number | null>((acc, t) => (t.toQty != null ? Math.max(acc ?? 0, t.toQty) : acc), null);
    const end = new Date(Date.now() + 365 * 86_400_000).toISOString().slice(0, 10);
    const productName = PRODUCTS.find((p) => p.id === productId)?.name ?? productId;
    const row: NewContract = {
      name: `${c.name} · ${productName} · ${o.label.replace(" · 레퍼런스 연동", "")}`,
      destination_country: c.id,
      country_name: c.name,
      buyer_id: null,
      buyer_name: null,
      product_id: products.find((p) => p.model_name.toLowerCase().includes(productName.toLowerCase().split(" ")[0]!))?.id ?? null,
      distributor_relationship_id: null,
      sim_product: productId,
      sim_country: c.id,
      currency: "USD",
      start_date: today,
      end_date: end,
      demo_start: null,
      demo_end: null,
      demo_qty: 0,
      license_label: c.id === "IN" ? "CDSCO MD-15" : c.id === "TW" ? "TFDA 허가" : "인허가",
      license_start: null,
      license_end: null,
      license_done: false,
      quota_start: today,
      tiers: ts.map((t) => ({ fromQty: t.fromQty, toQty: t.toQty, price: t.price, freeUnits: 0 })),
      free: { ...o.free },
      target_qty: lastTo ?? o.orderQty ?? null,
      sim_snapshot: {
        offer_label: o.label,
        order_qty: o.orderQty,
        rate: o.rateOverride ?? c.rate,
        floor_company: c.floorCompany,
        fx: c.fx,
        cost_krw: c.costKrw,
      },
      status: "active",
      note: o.note,
      legacy_id: null,
    };
    try {
      const created = await contractsRepo.create(row);
      refreshDb();
      setUrl({ case: { kind: "ct", id: created.id }, stage: 2 });
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }

  // ── 사이드바 렌더 (MANIFEST 스파인) ──
  const caseBtn = (active: boolean) =>
    "relative block w-full rounded-md border-l-[3px] px-3 py-2 text-left transition-colors " +
    (active ? "border-brass bg-brass/10" : "border-transparent hover:bg-white/[0.045]");

  const activeContracts = contracts.filter((c) => c.status === "active");
  const closedContracts = contracts.filter((c) => c.status !== "active");

  // 스테이지 콘텐츠에서 쓸 시뮬레이터 대상 (sim 케이스 or 계약의 sim 연결)
  const simTarget: { product: ProductId; country?: string } | null =
    cs.kind === "sim"
      ? { product: cs.product, country: cs.country }
      : selectedContract?.sim_product && PRODUCTS.some((p) => p.id === selectedContract.sim_product)
        ? { product: selectedContract.sim_product as ProductId, country: selectedContract.sim_country ?? undefined }
        : null;

  // 계약 케이스: 시뮬 연결이 있을 때만 stage① 활성(연결 없으면 데드엔드였음). 나머지 단계는 항상.
  const ctHasSim = Boolean(selectedContract?.sim_product && PRODUCTS.some((p) => p.id === selectedContract.sim_product));
  const stageEnabled = (no: number) =>
    cs.kind === "ct" ? (no === 1 ? ctHasSim : true) : cs.kind === "sim" ? no === 1 : false;

  return (
    <div className="flex min-h-[calc(100vh-52px)] bg-paper">
      {/* ── 좌측 케이스 목록 = 매니페스트 스파인 ── */}
      <aside className="sticky top-[52px] flex h-[calc(100vh-52px)] w-72 shrink-0 flex-col self-start border-r border-white/10 bg-ink text-onink">
        <div className="border-b border-white/10 px-[18px] py-4">
          <h1 className="font-display text-[15px] font-semibold leading-tight tracking-tight text-white">
            수출 워크데스크
          </h1>
          <p className="mt-1 flex flex-wrap items-center gap-1.5 text-[11px] text-onink-mut">
            <span className="text-onink">시뮬</span>
            <span className="text-brass">→</span>
            <span className="text-onink">계약</span>
            <span className="text-brass">→</span>
            <span className="text-onink">선적</span>
            <span className="text-brass">→</span>
            <span className="text-onink">실적</span>
          </p>
          <span className="mt-3 inline-flex items-center gap-1.5 rounded border border-brass px-2 py-[3px] text-[10px] font-bold uppercase tracking-[0.09em] text-brass">
            <span className="h-1.5 w-1.5 rounded-full bg-brass" />
            내부 대외비
          </span>
        </div>

        <nav className="flex-1 space-y-5 overflow-y-auto p-3">
          <button
            onClick={() => setUrl({ case: { kind: "home" }, stage: 1 })}
            className={
              "flex w-full items-center gap-2.5 rounded-md border px-3 py-2.5 text-[13px] font-medium transition-colors " +
              (cs.kind === "home"
                ? "border-brass bg-brass/10 text-white"
                : "border-white/10 bg-white/[0.03] text-onink hover:bg-white/[0.06]")
            }
          >
            <span className="text-brass">◎</span>오늘의 워크데스크
          </button>

          <div>
            <p className="mb-2 flex items-center justify-between px-2 text-[10px] font-bold uppercase tracking-[0.13em] text-onink-dim">
              <span>계약 · 진행 중</span>
              <span className="font-mono tracking-normal text-brass">{String(activeContracts.length).padStart(2, "0")}</span>
            </p>
            {activeContracts.length === 0 && (
              <p className="px-2 text-[11px] text-onink-dim">아직 없음 — 시뮬레이션에서 "계약 등록"</p>
            )}
            {activeContracts.map((c) => {
              const pr = contractProgress(contractRowToModel(c), salesAll.filter((s) => s.contract_id === c.id).map(saleToModel));
              const active = cs.kind === "ct" && cs.id === c.id;
              const ratio = pr.ratio != null ? Math.min(1, Math.max(0, pr.ratio)) : 0;
              return (
                <button key={c.id} onClick={() => setUrl({ case: { kind: "ct", id: c.id }, stage: 2 })} className={caseBtn(active)}>
                  <span className={"block truncate text-[13px] " + (active ? "font-semibold text-white" : "font-medium text-onink")}>{c.name}</span>
                  <span className="mt-0.5 block text-[10.5px] text-onink-mut">
                    {c.country_name ?? c.destination_country} · 달성 {pr.ratio != null ? pct(pr.ratio) : "—"} · 딜{" "}
                    {deals.filter((d) => d.contract_id === c.id && d.status !== "cancelled").length}건
                  </span>
                  <span className="mt-1.5 block h-[3px] overflow-hidden rounded-sm bg-white/10">
                    <i
                      className={"block h-full rounded-sm " + (pr.ratio != null && pr.ratio >= 1 ? "bg-gain" : "bg-brass")}
                      style={{ width: `${ratio * 100}%` }}
                    />
                  </span>
                </button>
              );
            })}
            {closedContracts.length > 0 && (
              <details className="mt-1.5">
                <summary className="cursor-pointer px-2 text-[11px] text-onink-dim hover:text-onink-mut">종료·취소 {closedContracts.length}건</summary>
                {closedContracts.map((c) => (
                  <button key={c.id} onClick={() => setUrl({ case: { kind: "ct", id: c.id }, stage: 2 })} className={caseBtn(cs.kind === "ct" && cs.id === c.id)}>
                    <span className="block truncate text-[13px] text-onink-mut">{c.name}</span>
                  </button>
                ))}
              </details>
            )}
          </div>

          <div>
            <p className="mb-2 flex items-center justify-between px-2 text-[10px] font-bold uppercase tracking-[0.13em] text-onink-dim">
              <span>협상 중 · 시뮬레이션</span>
              <span className="font-mono tracking-normal text-brass">{String(simCases.length).padStart(2, "0")}</span>
            </p>
            {simCases.map((sc) => {
              const active = cs.kind === "sim" && cs.product === sc.product && cs.country === sc.country;
              return (
                <button
                  key={`${sc.product}:${sc.country}`}
                  onClick={() => setUrl({ case: { kind: "sim", product: sc.product, country: sc.country }, stage: 1 })}
                  className={caseBtn(active)}
                >
                  <span className={"block truncate text-[13px] " + (active ? "font-semibold text-white" : "font-medium text-onink-mut")}>
                    {sc.productName} <span className="text-onink-dim">· {sc.countryName}</span>
                  </span>
                </button>
              );
            })}
          </div>

          <button
            onClick={() => setUrl({ case: { kind: "tl" }, stage: 1 })}
            className={
              "flex w-full items-center gap-2.5 rounded-md border px-3 py-2.5 text-[13px] font-medium transition-colors " +
              (cs.kind === "tl"
                ? "border-brass bg-brass/10 text-white"
                : "border-white/10 bg-white/[0.03] text-onink hover:bg-white/[0.06]")
            }
          >
            <span className="text-brass">◷</span>타임라인 · 런칭 프로그램
          </button>

          <button
            onClick={() => setUrl({ case: { kind: "all" }, stage: 1 })}
            className={
              "flex w-full items-center gap-2.5 rounded-md border px-3 py-2.5 text-[13px] font-medium transition-colors " +
              (cs.kind === "all"
                ? "border-brass bg-brass/10 text-white"
                : "border-white/10 bg-white/[0.03] text-onink hover:bg-white/[0.06]")
            }
          >
            <span className="text-brass">◫</span>전체 현황 · 전 계약·실적
          </button>
        </nav>

        <div className="border-t border-white/10 px-[18px] py-4 text-[10.5px] text-onink-dim">
          <div className="text-onink-mut">마진 수식 v1.1 · 값 수정 시 자동 저장</div>
          {saveMsg && (
            <div className="mt-1 flex items-center gap-1.5 text-gain">
              <span className="h-1.5 w-1.5 rounded-full bg-gain" />
              {saveMsg}
            </div>
          )}
          <div className="mt-2 flex gap-3">
            <Link to="/deals" className="text-onink-mut underline decoration-white/20 hover:text-onink">전체 딜 목록</Link>
          </div>
        </div>
      </aside>

      {/* ── 본문 ── */}
      <main className="min-w-0 flex-1 px-6 py-5">
        <ConfigBanner />
        {error && <p className="mb-2 rounded border border-risk/30 bg-risk-tint px-2 py-1 text-xs text-risk">{error}</p>}

        {cs.kind === "home" ? (
          <ActionHome
            contracts={contracts}
            sales={salesAll}
            deals={deals}
            simCount={simCases.length}
            today={today}
            onOpenContract={(id, stg) => setUrl({ case: { kind: "ct", id }, stage: stg })}
            onOpenAll={() => setUrl({ case: { kind: "all" }, stage: 1 })}
            onOpenSim={() => {
              const first = simCases[0];
              if (first) setUrl({ case: { kind: "sim", product: first.product, country: first.country }, stage: 1 });
            }}
          />
        ) : cs.kind === "all" ? (
          <>
            <ManagementDeck contracts={contracts} sales={salesAll} products={products} />
            <ConsolidatedView contracts={contracts} sales={salesAll} products={products} />
          </>
        ) : cs.kind === "tl" ? (
          // 마지막 else 는 STAGES 레일 캐치올이다 — tl 브랜치는 반드시 그 앞에 온다.
          <TimelineBoard
            contracts={contracts}
            deals={deals}
            today={today}
            onOpenContract={(id, stg) => setUrl({ case: { kind: "ct", id }, stage: stg })}
          />
        ) : (
          <>
            {/* 단계 레일 */}
            <div className="mb-4 flex flex-wrap items-stretch gap-1">
              {STAGES.map((s, i) => {
                const enabled = stageEnabled(s.no);
                const active = stage === s.no;
                const pendingBadge = s.no === 4 && pendingConfirmCount > 0;
                const badge =
                  s.no === 2 && selectedContract && !selectedContract.license_done && selectedContract.license_label
                    ? selectedContract.license_label + " 미완"
                    : s.no === 3 && selectedContract
                      ? `딜 ${contractDeals.filter((d) => d.status !== "cancelled").length}건`
                      : s.no === 4
                        ? pendingConfirmCount > 0
                          ? `확정 대기 ${pendingConfirmCount}건`
                          : progress?.ratio != null
                            ? `달성 ${pct(progress.ratio)}`
                            : null
                        : null;
                return (
                  <div key={s.no} className="flex items-center gap-1">
                    <button
                      onClick={() => enabled && setUrl({ stage: s.no, deal: null })}
                      disabled={!enabled}
                      title={enabled ? s.hint : "계약 등록 후 열립니다"}
                      className={
                        "rounded-lg border px-3 py-2 text-left transition-colors " +
                        (active
                          ? "border-ink bg-ink text-white"
                          : enabled
                            ? "border-line bg-surface text-graphite hover:bg-surface-2"
                            : "cursor-not-allowed border-line bg-surface-2 text-graphite-mut")
                      }
                    >
                      <span className="block text-xs font-bold">
                        {s.no}. {s.label}
                      </span>
                      <span className={"block text-[10px] " + (active ? "text-onink-mut" : pendingBadge ? "font-semibold text-warn" : "text-graphite-mut")}>{badge ?? s.hint}</span>
                    </button>
                    {i < STAGES.length - 1 && <span className="text-line-strong">▶</span>}
                  </div>
                );
              })}
            </div>

            {/* 케이스 헤더 */}
            <h2 className="mb-3 font-display text-xl font-semibold tracking-tight text-graphite">
              {cs.kind === "ct"
                ? (selectedContract?.name ?? "계약을 찾을 수 없습니다")
                : `${PRODUCTS.find((p) => p.id === cs.product)?.name ?? cs.product} — ${
                    simCases.find((x) => x.product === cs.product && x.country === cs.country)?.countryName ?? cs.country
                  } 협상 시뮬레이션`}
            </h2>

            {/* ① 시뮬레이션 */}
            {stage === 1 &&
              (desk && simTarget ? (
                <Simulator
                  key={`sim-${simTarget.product}-${simTarget.country ?? ""}`}
                  product={desk.products[simTarget.product]}
                  initialCountryId={simTarget.country}
                  onChange={(next) =>
                    setDesk((p) => (p ? { ...p, products: { ...p.products, [simTarget.product]: next } } : p))
                  }
                  onRegisterContract={(c, o) => void registerContract(simTarget.product, c, o)}
                />
              ) : (
                <p className="text-sm text-graphite-mut">
                  {desk ? "이 계약은 시뮬레이터와 연결돼 있지 않습니다. (직접 등록된 계약)" : "시뮬레이션 상태 불러오는 중…"}
                </p>
              ))}

            {/* ② 계약 */}
            {stage === 2 &&
              (selectedContract ? (
                <ContractCard
                  key={selectedContract.id}
                  contract={selectedContract}
                  sales={contractSales.map(saleToModel)}
                  partners={partners}
                  products={products}
                  today={today}
                  onChanged={(row) => setContracts((prev) => prev.map((x) => (x.id === row.id ? row : x)))}
                  onDeleted={() => {
                    refreshDb();
                    setUrl({ case: { kind: "home" }, stage: 1 });
                  }}
                />
              ) : (
                <p className="text-sm text-graphite-mut">계약 케이스를 선택하세요.</p>
              ))}

            {/* ③ 선적 딜 */}
            {stage === 3 &&
              (selectedContract ? (
                dealId ? (
                  <div>
                    <p className="mb-2">
                      <button onClick={() => setUrl({ deal: null })} className="text-sm text-graphite-mut underline hover:text-graphite">
                        ← {selectedContract.name} 딜 목록
                      </button>
                    </p>
                    <DealWorkspace dealId={dealId} embedded onChanged={refreshDb} />
                  </div>
                ) : (
                  <div className="rounded-lg border border-line bg-surface p-4">
                    <div className="mb-2 flex items-center justify-between">
                      <h3 className="flex items-center gap-2 text-sm font-bold text-graphite before:h-3 before:w-[3px] before:rounded-sm before:bg-brass before:content-['']">
                        연결 선적 딜 — 계약 조건(국가·통화·구간 단가)이 새 딜에 프리필됩니다
                      </h3>
                      <Link
                        to={`/deals/new?contract=${selectedContract.id}`}
                        className="rounded border border-gain/30 bg-gain-tint px-3 py-1.5 text-sm font-semibold text-gain hover:bg-gain/15"
                      >
                        + 새 선적 딜 만들기
                      </Link>
                    </div>
                    <table className="w-full border-collapse">
                      <thead>
                        <tr>
                          {["Deal No.", "제목", "상태", "생성일"].map((h) => (
                            <th key={h} className="border border-line bg-surface-2 px-2 py-1.5 text-left text-[11px] font-semibold uppercase tracking-wide text-graphite-mut">
                              {h}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {contractDeals.length === 0 && (
                          <tr>
                            <td colSpan={4} className="border border-line px-3 py-4 text-sm text-graphite-mut">
                              연결된 딜이 없습니다. "새 선적 딜 만들기"로 첫 선적을 시작하세요.
                            </td>
                          </tr>
                        )}
                        {contractDeals.map((d) => (
                          <tr key={d.id} className="cursor-pointer hover:bg-surface-2" onClick={() => setUrl({ deal: d.id })}>
                            <td className="border border-line px-2 py-1.5 text-sm">
                              <code className="font-mono text-graphite">{d.deal_no}</code>
                            </td>
                            <td className="border border-line px-2 py-1.5 text-sm text-graphite">{d.title ?? "—"}</td>
                            <td className="border border-line px-2 py-1.5 text-sm">
                              <span className={"rounded px-1.5 py-0.5 text-xs font-medium " + (d.status === "done" ? "bg-gain-tint text-gain" : d.status === "cancelled" ? "bg-surface-2 text-graphite-mut" : "bg-brass-tint text-brass-deep")}>
                                {STATUS_LABEL[d.status]}
                              </span>
                            </td>
                            <td className="border border-line px-2 py-1.5 font-mono text-xs text-graphite-mut">{d.created_at.slice(0, 10)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {progress?.nextTierAt != null && (
                      <p className="mt-2 text-[11px] text-graphite-mut">
                        누적 확정 {usd(progress.sold)}대 — 다음 구간 진입까지 {usd(progress.nextTierAt - progress.sold)}대. 새 딜의
                        단가 제안은 누적+이번 수량이 걸리는 구간을 따릅니다.
                      </p>
                    )}
                  </div>
                )
              ) : (
                <p className="text-sm text-graphite-mut">계약 케이스를 선택하세요.</p>
              ))}

            {/* ④ 실적 */}
            {stage === 4 &&
              (selectedContract ? (
                <ContractLedger contract={selectedContract} sales={contractSales} deals={contractDeals} onRefresh={refreshDb} />
              ) : (
                <p className="text-sm text-graphite-mut">계약 케이스를 선택하세요.</p>
              ))}
          </>
        )}
      </main>
    </div>
  );
}
